import React, { useState, useEffect } from 'react';
import '../../CSS/Carousel.css';
import rive_noire from '../../images/rive_noire.png';
import ouidah from '../../images/ouidah.png';
import ganvie from '../../images/ganvie.jpg';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useLanguageData } from '../../hooks/useLanguageData';

const Carousel = ({ arrowPosition = "right", source = "Accueil" }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [slidesAccueil, setSlidesAccueil] = useState([]);
    
    // Utilisation du hook useLanguageData pour les bannières
    const { data, loading, error } = useLanguageData('/api/v1/embassy/banner');

    const formatTitle = (title, textSurbrillance) => {
        if (!title && !textSurbrillance) return '';
        
        // Si pas de texte de surbrillance, retourner le titre normal avec formatage
        if (!textSurbrillance) {
            const words = title.split(' ');
            if (words.length <= 5) {
                return title;
            }
            
            const firstLine = words.slice(0, 5).join(' ');
            const secondLine = words.slice(5).join(' ');
            
            return (
                <>
                    {firstLine} <br /> {secondLine}
                </>
            );
        }

        // Combiner le titre et le texte de surbrillance
        const fullText = `${title} ${textSurbrillance}`;
        const words = fullText.split(' ');
        
        // Si le texte total fait 5 mots ou moins, tout afficher sur une ligne
        if (words.length <= 5) {
            return (
                <>
                    {title + ' '}
                    <span className="highlighted-text">
                        {textSurbrillance}
                    </span>
                </>
            );
        }
        
        // Pour les textes longs, essayer de garder le texte de surbrillance intact
        const titleWords = title.split(' ');
        const surbrillanceWords = textSurbrillance.split(' ');
        
        // Calculer combien de mots peuvent tenir sur la première ligne
        const wordsFirstLine = 5;
        const remainingWords = words.length - wordsFirstLine;
        
        // Si le texte de surbrillance tient sur la deuxième ligne
        if (surbrillanceWords.length <= remainingWords) {
            // Mettre le titre sur la première ligne et la surbrillance sur la deuxième
            return (
                <>
                    {title}
                    <br />
                    <span className="highlighted-text">
                        {textSurbrillance}
                    </span>
                </>
            );
        } 
        // Si le texte de surbrillance doit être coupé entre les deux lignes
        else {
            // Calculer combien de mots de la surbrillance peuvent aller sur la première ligne
            const availableFirstLine = wordsFirstLine - titleWords.length;
            if (availableFirstLine > 0) {
                const firstLineSurbrillance = surbrillanceWords.slice(0, availableFirstLine).join(' ');
                const secondLineSurbrillance = surbrillanceWords.slice(availableFirstLine).join(' ');
                
                return (
                    <>
                        {title + ' '}
                        <span className="highlighted-text">
                            {firstLineSurbrillance}
                        </span>
                        <br />
                        <span className="highlighted-text">
                            {secondLineSurbrillance}
                        </span>
                    </>
                );
            } else {
                // Si pas de place sur la première ligne, mettre toute la surbrillance sur la deuxième
                return (
                    <>
                        {title}
                        <br />
                        <span className="highlighted-text">
                            {textSurbrillance}
                        </span>
                    </>
                );
            }
        }
    };

    // slidesVisiter reste local (pas besoin d'API)
    const slidesVisiter = [
        { id: 1, image: rive_noire, title: 'Rivière Noire', description: '', link: '#' },
        { id: 2, image: ouidah, title: 'La porte du non retour', description: '', link: '#' },
        { id: 3, image: ganvie, title: 'Ganvié', description: '', link: '#' },
    ];

    // Transformer les données API en slides
    useEffect(() => {
        if (source === "Accueil" && data?.data) {
            // console.log('📊 Données API bannières (avec langue):', data);
            
            const mappedSlides = data.data.map(item => ({
                id: item.id || Math.random(),
                image: item.bannerImage,
                title: item.title || '',
                textSurbrillance: item.textSurbrillance || '',
                description: item.textButton || '',
                link: item.targetLink || '#',
            }));
            
            setSlidesAccueil(mappedSlides);
        }
    }, [data, source]);

    // Utiliser slidesAccueil (API) ou slidesVisiter
    const slides = source === "Visiter" ? slidesVisiter : slidesAccueil;

    // Autoplay du carousel - seulement s'il y a des slides
    useEffect(() => {
        if (slides.length > 0) {
            const interval = setInterval(() => {
                setCurrentSlide(prev => (prev + 1) % slides.length);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [slides.length]);

    const nextSlide = () => {
        if (slides.length > 0) {
            setCurrentSlide(prev => (prev + 1) % slides.length);
        }
    };
    
    const prevSlide = () => {
        if (slides.length > 0) {
            setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
        }
    };

    // Composant de spinner de chargement
    const LoadingSpinner = () => (
        <div className="carousel-loading">
            <div className="carousel-spinner"></div>
            <style>
                {`
                    .carousel-loading {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        height: 400px;
                        background: #f8f9fa;
                    }
                    .carousel-spinner {
                        width: 50px;
                        height: 50px;
                        border: 4px solid #f3f3f3;
                        border-top: 4px solid #1a3c5e;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                    }
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style> 
        </div>
    );

    // Si en chargement pour le carousel Accueil
    if (source === "Accueil" && loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="carousel">
            <div className="carousel-container">
                {slides.length > 0 ? (
                    slides.map((slide, index) => (
                        <div 
                            key={slide.id} 
                            className={`carousel-slide ${index === currentSlide ? 'active' : ''}`} 
                            style={{ backgroundImage: `url(${slide.image})` }}
                        >
                            <div className="slide-overlay"></div>
                            <div className="slide-content">
                                <div className="title-container">
                                    <h2
                                        className="slide-title"
                                        style={{ textAlign: source === 'Visiter' ? 'center' : 'left' }}
                                    >
                                        {formatTitle(slide.title, slide.textSurbrillance)}
                                    </h2>
                                </div>

                                {slide.description && (
                                    <a href={slide.link} className="slide-button">
                                        {slide.description}
                                    </a>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    // Carousel vide mais visible (avec un fond gris par exemple)
                    <div className="carousel-slide active" style={{ backgroundColor: '#f0f0f0' }}>
                        <div className="slide-overlay"></div>
                        <div className="slide-content">
                            <div className="title-container">
                                <h2 className="" style={{ color: '#999', fontStyle: 'italic' }}>
                                    Aucune bannière disponible
                                </h2>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Flèches - toujours visibles */}
            <button className={`carousel-btn carousel-btn-prev ${arrowPosition}`} onClick={prevSlide}>
                <FaChevronLeft className="carousel-icon" color="white" size={15} />
            </button>
            <button className={`carousel-btn carousel-btn-next ${arrowPosition}`} onClick={nextSlide}>
                <FaChevronRight className="carousel-icon" color="white" size={15} />
            </button>
        </div>
    );
};

export default Carousel;